import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function SharedShoppingList() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = params.get('token');
  const kitchen = params.get('kitchen');
  const week = params.get('week');

  useEffect(() => {
    if (!token || !kitchen || !week) {
      setError(t('shared.invalidLink'));
      return;
    }

    const storageKey = `shared-list-${token}`;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setChecked(JSON.parse(saved));
    } catch {}

    const apiBase = process.env.REACT_APP_API_URL || '';
    fetch(`${apiBase}/api/shopping-list/shared?token=${encodeURIComponent(token)}&kitchen=${encodeURIComponent(kitchen)}&week=${encodeURIComponent(week)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load list');
        return res.json();
      })
      .then(setData)
      .catch(() => setError(t('shared.failedToLoad')));
  }, [token, kitchen, week]);

  const handleToggle = (name) => {
    setChecked((prev) => {
      const next = { ...prev };
      if (next[name]) delete next[name];
      else next[name] = true;
      const storageKey = `shared-list-${token}`;
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  if (error) {
    return (
      <div className="shared-list-page">
        <h1 className="shared-list-title">{t('shared.title')}</h1>
        <p className="shared-list-error">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="shared-list-page">
        <p className="shared-list-loading">{t('shared.loading')}</p>
      </div>
    );
  }

  if (data.items.length === 0) {
    return (
      <div className="shared-list-page">
        <h1 className="shared-list-title">{t('shared.title')}</h1>
        <p className="shared-list-week">{data.week}</p>
        <p>{t('shared.nothingToBuy')}</p>
      </div>
    );
  }

  const freshItems = data.items.filter((i) => i.section === 'fresh');
  const cupboardItems = data.items.filter((i) => i.section === 'cupboard');
  const checkedCount = Object.keys(checked).length;

  const renderSection = (title, icon, sectionItems) => {
    if (sectionItems.length === 0) return null;
    return (
      <div className="shopping-section" key={title}>
        <div className="shopping-section-header">{icon} {title}</div>
        {sectionItems.map((item) => {
          const isChecked = !!checked[item.name];
          return (
            <div key={item.name} className={`shopping-item shopping-item--shopmode${isChecked ? ' shopping-item--checked' : ''}`}
              onClick={() => handleToggle(item.name)} style={{ cursor: 'pointer' }}>
              <span className={`shopping-check shopping-check--shopmode${isChecked ? ' shopping-check--done' : ''}`}>
                {isChecked ? '✓' : ''}
              </span>
              <span className={`shopping-item-name shopping-item-name--shopmode${isChecked ? '' : ''}`}>{item.name}</span>
              {!isChecked && <span className="shopping-item-need">{t('shared.need', { amount: item.shortage })}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="shared-list-page">
      <h1 className="shared-list-title">{t('shared.title')}</h1>
      <p className="shared-list-week">{data.week}</p>
      <span className="shopping-counter">{t('shared.itemsCounter', { checked: checkedCount, total: data.items.length })}</span>
      {renderSection(t('shared.fresh'), '🥬', freshItems)}
      {renderSection(t('shared.cupboard'), '🍯', cupboardItems)}
    </div>
  );
}

export default SharedShoppingList;
